// Taken from https://github.com/ankurk91/vue-flatpickr-component

import Flatpickr from "flatpickr";
import $ from "jquery";
// You have to import css yourself

// Events to emit, copied from flatpickr source
const includedEvents = [
    "onChange",
    "onClose",
    "onDestroy",
    "onKeyDown",
    "onMonthChange",
    "onOpen",
    "onYearChange"
];

// Let's not emit these events by default
const excludedEvents = [
    "onValueUpdate",
    "onDayCreate",
    "onParseConfig",
    "onReady",
    "onPreCalendarPosition"
];

// Keep a copy of all events for later use
const allEvents = includedEvents.concat(excludedEvents);

// Passing these properties in `set()` method will cause flatpickr to trigger some callbacks
const configCallbacks = ["locale", "showMonths", "onClose", "onOpen"];

const camelToKebab = string => {
    return string.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
};

const arrayify = obj => {
    return obj instanceof Array ? obj : [obj];
};

const cloneObject = obj => {
    return Object.assign({}, obj);
};

const defaultOnClose = (selectedDates, dateStr, instance) => {
    if (instance.config.altInput) {
        const dateStringToSave = instance.altInput.value;
        instance.setDate(dateStringToSave, true, instance.config.altFormat);
    }
};

const defaultOnOpen = (selectedDates, dateStr, instance) => {
    if (instance.config.enableTime) {
        $(instance.hourElement).add(instance.minuteElement).prop("disabled", true);
    }
};

export default {
    name: "flat-pickr",
    template: "<input type=\"text\" @input=\"onInput\" data-input>",
    props: {
        value: {
            default: null,
            required: true,
            validator(value) {
                return value === null || value instanceof Date || typeof value === "string" || value instanceof String || value instanceof Array || typeof value === "number";
            }
        },
        // https://chmln.github.io/flatpickr/options/
        config: {
            type: Object,
            default: () => ({
                wrap: false,
                defaultDate: null,
                onOpen: defaultOnOpen,                
                onClose: defaultOnClose
            })
        },
        events: {
            type: Array,
            default: () => includedEvents
        },
        isDateTimePicker: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            /**
             * The flatpickr instance
             */
            fp: null
        };
    },
    mounted() {
        // Return early if flatpickr is already loaded
        /* istanbul ignore if */
        if (this.fp) {
            return;
        }

        // Don't mutate original object on parent component
        const safeConfig = cloneObject(this.config);

        // Inject our methods into events array
        this.events.forEach(hook => {
            safeConfig[hook] = arrayify(safeConfig[hook] || []).concat((...args) => {
                this.$emit(camelToKebab(hook), ...args);
            });
        });

        // Set initial date without emitting any event
        safeConfig.defaultDate = this.value || safeConfig.defaultDate;

        if (this.isDateTimePicker) {
            safeConfig.enableTime = true;

            if (window.AcademIt.Locale === "ru") {
                safeConfig.altFormat = "d.m.Y H:i";
                safeConfig.dateFormat = "Y-m-d H:i";
            } else {
                safeConfig.altFormat = "m/d/Y h:i K";
                safeConfig.dateFormat = "Y-m-d h:i K";
            }

            // Fix: calendar closes after click on time controls inside Bootstrap 4 modal
            // https://github.com/flatpickr/flatpickr/issues/1352
            if ($(this.getElem()).closest(".modal").length > 0) {
                safeConfig.static = true;
            }
        }

        this.addDefaultToConfig(safeConfig);

        // Init flatpickr
        this.fp = new Flatpickr(this.getElem(), safeConfig);
    },
    methods: {
        /**
         * Get the HTML node where flatpickr to be attached
         * Bind on parent element if wrap is true
         */
        getElem() {
            return this.config.wrap ? this.$el.parentNode : this.$el;
        },

        /**
         * Watch for value changed by date-picker itself and notify parent component
         *
         * @param event
         */
        onInput(event) {
            this.$emit("input", event.target.value);
        },

        addDefaultToConfig(safeConfig) {
            if (!safeConfig.onClose) {
                safeConfig.onClose = [defaultOnClose];
            } else if (typeof safeConfig.onClose === "function") {
                safeConfig.onClose = [defaultOnClose, safeConfig.onClose];
            } else {
                const newOnClose = safeConfig.onClose.slice(0);
                newOnClose.push(defaultOnClose);
                safeConfig.onClose = newOnClose;
            }

            if (!safeConfig.onOpen) {
                safeConfig.onOpen = [defaultOnOpen];
            } else if (typeof safeConfig.onOpen === "function") {
                safeConfig.onOpen = [defaultOnOpen, safeConfig.onOpen];
            } else {
                const newOnOpen = safeConfig.onOpen.slice(0);
                newOnOpen.push(defaultOnOpen);
                safeConfig.onOpen = newOnOpen;
            }
        }
    },
    watch: {
        /**
         * Watch for any config changes and redraw date-picker
         *
         * @param newConfig Object
         */
        config: {
            deep: true,
            handler(newConfig) {
                const safeConfig = cloneObject(newConfig);
                // Workaround: Don't pass hooks to configs again otherwise
                // previously registered hooks will stop working
                // Notice: we are looping through all events
                // This also means that new callbacks can not passed once component has been initialized
                allEvents.forEach(hook => {
                    delete safeConfig[hook];
                });

                this.addDefaultToConfig(safeConfig);
                this.fp.set(safeConfig);

                // Workaround: Allow to change locale dynamically
                configCallbacks.forEach(name => {
                    if (typeof safeConfig[name] !== "undefined") {
                        this.fp.set(name, safeConfig[name]);
                    }
                });
            }
        },
        /**
         * Watch for changes from parent component and update DOM
         *
         * @param newValue
         */
        value(newValue) {
            // Prevent updates if v-model value is same as input's current value
            if (newValue === this.$el.value) {
                return;
            }

            // Make sure we have a flatpickr instance
            this.fp &&
            // Notify flatpickr instance that there is a change in value
            this.fp.setDate(newValue, true);
        }
    },
    /**
     * Free up memory
     */
    beforeDestroy() {
        /* istanbul ignore else */
        if (this.fp) {
            this.fp.destroy();
            this.fp = null;
        }
    }
};